import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useColors } from '../../../../../theme/ColorModeContext';
import { StageSection, Prose, KeyPoints } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'What Is Starknet?',
    tagline: 'Ethereum scaled by proving execution instead of re-running it.',
};

// This stage expects 3 quiz questions configured in the admin panel, in this order:
//   Q0 - the scaling problem a validity rollup solves
//   Q1 - what a validity (STARK) proof actually lets Ethereum skip
//   Q2 - what a valid proof does and does not guarantee about the contract
export const blocks: StageBlock[] = [
    content('hero', () => {
        const colors = useColors();
        return (
            <StageSection gradientBorder eyebrow="Stage 1 of 8">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mb: 2 }}>
                    {['Validity Rollup', 'Cairo', 'STARK Proofs'].map((label) => (
                        <Chip
                            key={label}
                            label={label}
                            size="small"
                            variant="outlined"
                            sx={{ color: colors.accent.cyan, borderColor: colors.border.accent, backgroundColor: colors.accent.cyanGlow, fontFamily: '"Orbitron", sans-serif' }}
                        />
                    ))}
                </Box>
                <Typography variant="h2" sx={{
                    fontFamily: '"Orbitron", sans-serif',
                    fontSize: { xs: '1.9rem', md: '2.6rem' },
                    lineHeight: 1.1,
                    fontWeight: 800,
                    color: colors.text.primary,
                    mb: 2,
                }}>
                    Every Ethereum node re-executing every transaction doesn't scale.
                </Typography>
                <Prose>
                    Ethereum's security comes from thousands of independent nodes each re-running every transaction
                    and agreeing on the result. That's exactly what makes it slow and expensive: block space is
                    scarce because every honest node has to redo the same work.
                </Prose>
                <Prose>
                    Starknet takes a different approach to the same problem. Instead of asking Ethereum to re-execute
                    transactions, it asks Ethereum to check a mathematical proof that they were executed correctly.
                </Prose>
            </StageSection>
        );
    }),

    content('validity-rollup', () => (
        <StageSection title="A validity rollup, not an optimistic one">
            <Prose>
                Starknet is a Layer 2 <strong>validity rollup</strong>. Transactions are batched and executed
                off-chain by a sequencer, and a separate prover generates a succinct cryptographic proof (a STARK)
                that the batch was executed according to the rules. That proof, along with the resulting state root,
                is posted to a verifier contract on Ethereum.
            </Prose>
            <Prose>
                This is different from an optimistic rollup, which assumes execution was correct and only checks it
                if someone disputes it during a challenge window. A validity rollup never has to assume: the proof
                itself is the guarantee, checked the moment it's submitted.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('what-the-proof-buys', () => (
        <StageSection title="What the proof lets Ethereum skip">
            <Prose>
                Verifying a STARK proof is dramatically cheaper than re-running the thousands of transactions it
                attests to. Ethereum's verifier contract doesn't see the individual transactions at all; it sees a
                proof and a claimed new state root, and it either accepts or rejects that root based purely on
                whether the proof checks out.
            </Prose>
            <KeyPoints items={[
                <>Off-chain: a sequencer orders and executes transactions, changing Starknet's state.</>,
                <>Off-chain: a prover compresses that entire execution trace into one succinct proof.</>,
                <>On-chain: Ethereum verifies the proof and, if valid, accepts the new state root as final.</>,
            ]} />
        </StageSection>
    )),

    question(1),

    content('proof-does-not-fix-bugs', () => (
        <StageSection title="A proof of correct execution is not a proof of a safe contract">
            <Prose>
                It's worth being precise about what "correct" means here. The STARK proves that the sequencer
                applied the Cairo code exactly as written: every instruction, every storage write, every check.
                It says nothing about whether that Cairo code was written safely.
            </Prose>
            <Prose>
                If a contract is missing a caller check, or mishandles arithmetic, the proof will faithfully attest
                that the broken logic ran exactly as coded. Validity proofs remove the need to trust the sequencer's
                honesty about execution; they don't remove the need to write correct contracts. The rest of this
                track, and the Cairo security challenges that follow it, are about exactly that second problem.
            </Prose>
        </StageSection>
    )),

    question(2),
];
